import moment from "moment";
import { getWeekPeriodFromDate } from "./nutrition-helpers";
import _ from "lodash";
import { MUSCLE_GROUP_ARRAY, OUTCOME_STATUS } from "../constants";
import { formattedTarget } from "./actuals";
import { getExerciseName } from "../parts/modals/learn-more/blockHelpers";

const { COMPLETED, MISSED } = OUTCOME_STATUS;

const groupExercisesByName = (exercises) => {
  const uniqueNames = {};
  const result = [];

  for (const obj of exercises) {
    if (!uniqueNames[getExerciseName(obj)]) {
      result.push({
        name: getExerciseName(obj),
        numberOfSets: [],
        sessions: 0,
      });
      uniqueNames[getExerciseName(obj)] = true;
    }
  }

  return result;
};

const getWeeklyDates = (from, to, startingCalendarDay) => {
  let weeklyPeriods = {};

  let { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
    from,
    startingCalendarDay
  );

  while (endOfWeek.isSameOrBefore(moment(to).add(7, "days"))) {
    const period = `${startOfWeek.format("YYYY-MM-DD")} - ${moment(
      endOfWeek
    ).format("YYYY-MM-DD")}`;

    weeklyPeriods[period] = {
      startOfWeek: startOfWeek.toISOString(),
      endOfWeek: endOfWeek.toISOString(),
      utcDate: Number(startOfWeek.format("YYYYMMDD")),
      maxWeight: 0,
      topE1rm: 0,
      volume: 0,
      sessions: 0,
      outcomes: {},
      hasData: false,
    };

    startOfWeek.add(7, "days");
    endOfWeek.add(7, "days");
  }

  return weeklyPeriods;
};

const getBlockExerciseData = (
  weekExercises,
  populatedExercises,
  startingCalendarDay,
  intensityMeasure,
  activeUser,
  withWeight,
  fromMetric
) => {
  for (const exercise of weekExercises) {
    const name = getExerciseName(exercise);

    const defaultRepMax = _.get(
      exercise,
      exercise.type === "primary"
        ? `repMaximums.${exercise.primary}`
        : "secondaryRepMaximum",
      _.get(activeUser, "repMaximums.squat", 0)
    );

    const { units } = activeUser;
    const precision = units === "metric" ? 2 : 1;

    const { type } = exercise;

    const isRiR = intensityMeasure === "RiR";
    const isPowerlifting = type === "primary" || type === "secondary";

    const indexOfExercise = populatedExercises.findIndex(
      (item) => item.name === name
    );

    if (indexOfExercise >= 0) {
      const { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
        String(exercise.utcDate),
        startingCalendarDay
      );

      const period = `${startOfWeek.format("YYYY-MM-DD")} - ${moment(
        endOfWeek
      ).format("YYYY-MM-DD")}`;

      // Create object
      if (!populatedExercises[indexOfExercise].periods) {
        populatedExercises[indexOfExercise].periods = {};
      }

      // Deep clone weeklyPeriods for each exercise
      if (!populatedExercises[indexOfExercise].periods[period]) {
        populatedExercises[indexOfExercise].periods[period] = {
          startOfWeek: startOfWeek.toISOString(),
          maxWeight: 0,
          topE1rm: 0,
          volume: 0,
          sessions: 0,
          outcomes: {},
          hasData: false,
        };
        populatedExercises[indexOfExercise].periods[period].numberOfSets = [];
      }

      populatedExercises[indexOfExercise].periods[period].sessions += 1;

      if (
        populatedExercises[indexOfExercise].sessions <
        populatedExercises[indexOfExercise].periods[period].sessions
      ) {
        populatedExercises[indexOfExercise].sessions += 1;
      }

      const realNumberOfSets = [];
      let cummulativeSets = 0;
      exercise.sets.forEach((set) => {
        realNumberOfSets.push(Number(set.sets || set.rounds || 1));

        let maxWeight = 0;
        let topE1rm = 0;
        let volume = 0;

        if (set.outcomes) {
          // Update flag to show block of data or not
          populatedExercises[indexOfExercise].periods[period].hasData = true;

          // Fill the outcomes
          _.times(Number(set.sets || set.rounds || 1)).forEach(
            (setOrRoundIndex) => {
              if (set.outcomes[setOrRoundIndex]) {
                const { outcome, status } = set.outcomes[setOrRoundIndex];

                if (status === COMPLETED || status === MISSED) {
                  const { load, reps, e1rm } = outcome || {};

                  const percentRM = _.round(
                    (_.get(set, "load", 0) / defaultRepMax) * 100,
                    1
                  );

                  if (maxWeight < load && status === COMPLETED)
                    maxWeight = load;
                  if (topE1rm < e1rm && status === COMPLETED) topE1rm = e1rm;
                  if (status === COMPLETED)
                    volume += Math.round(Number(load) * Number(reps) || 0);
                  populatedExercises[indexOfExercise].periods[period].outcomes =
                    {
                      ...populatedExercises[indexOfExercise].periods[period]
                        .outcomes,
                      [populatedExercises[indexOfExercise].periods[period]
                        .sessions]: {
                        ...populatedExercises[indexOfExercise].periods[period]
                          .outcomes[
                          populatedExercises[indexOfExercise].periods[period]
                            .sessions
                        ],
                        [cummulativeSets + setOrRoundIndex]: {
                          target: formattedTarget(
                            { ...outcome, sets: null, rounds: null },
                            false,
                            percentRM,
                            "metric",
                            load
                              ? withWeight(
                                  _.round(fromMetric(load, units), precision),
                                  units
                                )
                              : 0,
                            intensityMeasure,
                            isRiR
                          ),
                          comment: set.comments
                            ? set.comments[setOrRoundIndex]
                            : null,
                          video: set.videos
                            ? set.videos[setOrRoundIndex]
                            : null,
                          status,
                          exerciseId: exercise._id,
                        },
                      },
                    };
                }
              } else {
                const { load } = set;

                const percentRM = _.round(
                  (_.get(set, "load", 0) / defaultRepMax) * 100,
                  1
                );

                // If outcome is missing, add placeholder
                populatedExercises[indexOfExercise].periods[period].outcomes = {
                  ...populatedExercises[indexOfExercise].periods[period]
                    .outcomes,
                  [populatedExercises[indexOfExercise].periods[period]
                    .sessions]: {
                    ...populatedExercises[indexOfExercise].periods[period]
                      .outcomes[
                      populatedExercises[indexOfExercise].periods[period]
                        .sessions
                    ],
                    [cummulativeSets + setOrRoundIndex]: {
                      target: formattedTarget(
                        { ...set, sets: null, rounds: null },
                        false,
                        percentRM,
                        "metric",
                        load
                          ? withWeight(
                              _.round(fromMetric(load, units), precision),
                              units
                            )
                          : 0,
                        intensityMeasure,
                        isRiR
                      ),
                      comment: null,
                      video: null,
                      status: "incomplete",
                    },
                  },
                };
              }
            }
          );
        }

        // Update maxWeight, top e1rm and volume
        if (
          populatedExercises[indexOfExercise].periods[period].maxWeight <
          maxWeight
        )
          populatedExercises[indexOfExercise].periods[period].maxWeight =
            maxWeight;
        if (
          populatedExercises[indexOfExercise].periods[period].topE1rm < topE1rm
        )
          populatedExercises[indexOfExercise].periods[period].topE1rm = topE1rm;
        populatedExercises[indexOfExercise].periods[period].volume += volume;

        cummulativeSets += set.sets || set.rounds || 1;
      });

      const totalSets = realNumberOfSets.reduce(
        (acc, currentValue) => acc + currentValue,
        0
      );
      populatedExercises[indexOfExercise].periods[period].numberOfSets.push(
        totalSets
      );

      // Update numberOfSets array based on all periods
      const sessions =
        populatedExercises[indexOfExercise].periods[period].sessions;
      for (let i = 0; i < sessions; i++) {
        const setsArray = [];
        for (const key in populatedExercises[indexOfExercise].periods) {
          if (populatedExercises[indexOfExercise].periods.hasOwnProperty(key)) {
            const sets =
              populatedExercises[indexOfExercise].periods[key].numberOfSets;
            if (sets && sets[i]) {
              setsArray.push(sets[i]);
            }
          }
        }
        populatedExercises[indexOfExercise].numberOfSets[i] = Math.max(
          ...setsArray
        );
      }
    }
  }
};

const addMissingPeriods = (exercises, weeklyPeriods) => {
  exercises.forEach((exercise) => {
    const exercisePeriods = Object.keys(exercise.periods);
    Object.keys(weeklyPeriods).forEach((period) => {
      if (!exercisePeriods.includes(period)) {
        exercise.periods[period] = {
          startOfWeek: weeklyPeriods[period].startOfWeek,
          maxWeight: 0,
          topE1rm: 0,
          volume: 0,
          sessions: 0,
          outcomes: {},
          hasData: false,
        };
      }
    });
  });
};

const addMissingOutcomes = (exercises) => {
  exercises.forEach((exercise) => {
    _.times(exercise.sessions).forEach((session) => {
      for (const period in exercise.periods) {
        // Check if outcomes object exists, if not, initialize it
        if (!exercise.periods[period].outcomes) {
          exercise.periods[period].outcomes = {};
        }

        if (!exercise.periods[period].outcomes[session + 1]) {
          exercise.periods[period].outcomes[session + 1] = {};
        }

        _.times(exercise.numberOfSets[session]).forEach((set) => {
          if (!exercise.periods[period].outcomes[session + 1][set]) {
            exercise.periods[period].outcomes[session + 1] = {
              ...exercise.periods[period].outcomes[session + 1],
              [set]: {
                target: null,
                comment: null,
                video: null,
              },
            };
          }
        });
      }
    });
  });
};

export const getBlockData = (
  weekExercises,
  from,
  to,
  startingCalendarDay,
  intensityMeasure,
  activeUser,
  withWeight,
  fromMetric
) => {
  const exercisesByName = groupExercisesByName(weekExercises);
  const weeklyPeriods = getWeeklyDates(from, to, startingCalendarDay);

  getBlockExerciseData(
    weekExercises,
    exercisesByName,
    startingCalendarDay,
    intensityMeasure,
    activeUser,
    withWeight,
    fromMetric
  );

  addMissingPeriods(exercisesByName, { ...weeklyPeriods });
  addMissingOutcomes(exercisesByName);

  return exercisesByName;
};

export const getPeriods = (from, to, startingCalendarDay) => {
  const weeklyPeriods = [];

  let { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
    from,
    startingCalendarDay
  );

  while (endOfWeek.isSameOrBefore(moment(to).add(7, "days"))) {
    const period = `${startOfWeek.format("MMM D")} - ${moment(endOfWeek).format(
      "MMM D"
    )}`;

    weeklyPeriods.push(period);

    startOfWeek.add(7, "days");
    endOfWeek.add(7, "days");
  }

  return weeklyPeriods;
};

const getMetricsExerciseData = (
  weekExercises,
  startingCalendarDay,
  exerciseFilters
) => {
  let periods = {};

  for (const exercise of weekExercises) {
    if (exerciseFilters.length > 0) {
      const name = getExerciseName(exercise);

      if (!exerciseFilters.includes(name)) {
        continue;
      }
    }

    const { startOfWeek, endOfWeek } = getWeekPeriodFromDate(
      String(exercise.utcDate),
      startingCalendarDay
    );

    const period = `${startOfWeek.format("YYYY-MM-DD")} - ${moment(
      endOfWeek
    ).format("YYYY-MM-DD")}`;

    // Create object
    if (!periods[period]) {
      periods[period] = {
        startOfWeek: startOfWeek.toISOString(),
        volume: 0,
        sets: {},
        reps: {},
      };
    }

    const { primaryMuscleGroup, secondaryMuscleGroup } = exercise;

    let totalSets = 0;
    let totalReps = 0;
    exercise.sets.forEach((set) => {
      let volume = 0;
      let repsSum = 0;
      let setsSum = 0;

      if (set.outcomes) {
        const keys = Object.keys(set.outcomes);

        keys.forEach((key) => {
          if (set.outcomes[key].outcome) {
            const { status } = set.outcomes[key];

            if (status === COMPLETED) {
              const { reps, load } = set.outcomes[key].outcome;
              volume += Math.round(Number(load) * Number(reps) || 0);
              repsSum += Number(reps) || 0;
              setsSum++;
            }
          }
        });
      }

      periods[period].volume += volume;
      totalSets += setsSum;
      totalReps += repsSum;
    });

    if (primaryMuscleGroup) {
      if (!periods[period].sets[primaryMuscleGroup]) {
        periods[period].sets[primaryMuscleGroup] = 0;
      }
      if (!periods[period].reps[primaryMuscleGroup]) {
        periods[period].reps[primaryMuscleGroup] = 0;
      }

      const numberOfSets = periods[period].sets[primaryMuscleGroup] || 0;
      const numberOfReps = periods[period].reps[primaryMuscleGroup] || 0;

      periods[period].sets[primaryMuscleGroup] = numberOfSets + totalSets || 0;
      periods[period].reps[primaryMuscleGroup] = numberOfReps + totalReps || 0;
    }

    if (secondaryMuscleGroup && secondaryMuscleGroup !== primaryMuscleGroup) {
      if (!periods[period].sets[secondaryMuscleGroup]) {
        periods[period].sets[secondaryMuscleGroup] = 0;
      }
      if (!periods[period].reps[secondaryMuscleGroup]) {
        periods[period].reps[secondaryMuscleGroup] = 0;
      }

      const numberOfSets = periods[period].sets[secondaryMuscleGroup] || 0;
      const numberOfReps = periods[period].reps[secondaryMuscleGroup] || 0;

      periods[period].sets[secondaryMuscleGroup] =
        numberOfSets + totalSets || 0;
      periods[period].reps[secondaryMuscleGroup] =
        numberOfReps + totalReps || 0;
    }
  }

  return periods;
};

const fillMissingKeys = (obj) => {
  const result = {};
  MUSCLE_GROUP_ARRAY.forEach((muscle) => {
    if (muscle !== "None") {
      result[muscle] = obj[muscle] || 0;
    }
  });
  return result;
};

export const getMetricsData = (
  weekExercises,
  startingCalendarDay,
  from,
  to,
  exerciseFilters = []
) => {
  const metricsData = getMetricsExerciseData(
    weekExercises,
    startingCalendarDay,
    exerciseFilters
  );

  const weeklyPeriods = getWeeklyDates(from, to, startingCalendarDay);

  Object.keys(weeklyPeriods).forEach((period) => {
    if (!metricsData.hasOwnProperty(period)) {
      metricsData[period] = {
        volume: 0,
        sets: {},
        reps: {},
        startOfWeek: weeklyPeriods[period].startOfWeek,
      };
    }
  });

  Object.keys(metricsData).forEach((period) => {
    metricsData[period].sets = fillMissingKeys(metricsData[period].sets);
    metricsData[period].reps = fillMissingKeys(metricsData[period].reps);
  });

  return metricsData;
};
